import { useState, useEffect, useMemo } from 'react';
import { Layers, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import type { SchemaFilter } from '../../core/types/workspaceTypes';

type SchemaPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Available schemas (union of source+target). */
  availableSchemas: string[];
  /** Current filter, or undefined for "All schemas". */
  value?: SchemaFilter;
  onApply: (next: SchemaFilter | undefined) => void;
  /** Whether system schemas are included. Apply commits this back. */
  includeSystemSchemas: boolean;
  onIncludeSystemSchemasChange: (next: boolean) => void;
};

export function SchemaPickerDialog({
  open,
  onOpenChange,
  availableSchemas,
  value,
  onApply,
  includeSystemSchemas,
  onIncludeSystemSchemasChange,
}: SchemaPickerDialogProps) {
  const [mode, setMode] = useState<'include' | 'exclude'>(value?.mode ?? 'include');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(value?.schemas ?? [])
  );
  const [search, setSearch] = useState('');
  const [includeSystem, setIncludeSystem] = useState(includeSystemSchemas);

  // Reset local state every time the dialog opens
  useEffect(() => {
    if (open) {
      setMode(value?.mode ?? 'include');
      setSelected(new Set(value?.schemas ?? []));
      setSearch('');
      setIncludeSystem(includeSystemSchemas);
    }
  }, [open, value, includeSystemSchemas]);

  const filteredSchemas = useMemo(() => {
    const lower = search.toLowerCase().trim();
    if (!lower) return availableSchemas;
    return availableSchemas.filter((s) => s.toLowerCase().includes(lower));
  }, [availableSchemas, search]);

  const allFilteredSelected =
    filteredSchemas.length > 0 && filteredSchemas.every((s) => selected.has(s));

  const toggleSchema = (schema: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(schema)) next.delete(schema);
      else next.add(schema);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const s of filteredSchemas) next.delete(s);
      } else {
        for (const s of filteredSchemas) next.add(s);
      }
      return next;
    });
  };

  const handleApply = () => {
    if (selected.size === 0) {
      onApply(undefined);
    } else {
      onApply({ mode, schemas: Array.from(selected) });
    }
    if (includeSystem !== includeSystemSchemas) {
      onIncludeSystemSchemasChange(includeSystem);
    }
    onOpenChange(false);
  };

  const handleClear = () => {
    setSelected(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="schema-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Choose schemas
          </DialogTitle>
          <DialogDescription>
            Limit the comparison to a specific set of schemas. The same selection applies to both source and target.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Mode + summary row */}
          <div className="flex items-center gap-2">
            <Select
              value={mode}
              onValueChange={(m) => setMode(m as 'include' | 'exclude')}
            >
              <SelectTrigger
                className="w-[120px]"
                data-testid="schema-picker-mode"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="include">Include</SelectItem>
                <SelectItem value="exclude">Exclude</SelectItem>
              </SelectContent>
            </Select>
            <Badge
              variant="secondary"
              className="text-xs"
              data-testid="schema-picker-selected-count"
            >
              {selected.size} selected
            </Badge>
            {selected.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 text-xs"
                onClick={handleClear}
                data-testid="schema-picker-clear"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search schemas..."
              className="pl-7 h-8 text-sm"
              data-testid="schema-picker-search"
            />
          </div>

          {/* Select all (filtered) */}
          {filteredSchemas.length > 0 && (
            <div className="flex items-center gap-2 px-2 py-1 border-b">
              <Checkbox
                id="schema-picker-select-all"
                checked={allFilteredSelected}
                onCheckedChange={handleSelectAll}
                data-testid="schema-picker-select-all"
              />
              <label
                htmlFor="schema-picker-select-all"
                className="text-xs text-muted-foreground cursor-pointer flex-1"
              >
                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                {search ? ` (${filteredSchemas.length} matching)` : ''}
              </label>
            </div>
          )}

          {/* Include system schemas */}
          <div className="flex items-center gap-2 px-1">
            <Checkbox
              id="schema-picker-include-system"
              checked={includeSystem}
              onCheckedChange={(checked) => setIncludeSystem(checked === true)}
              data-testid="schema-picker-include-system"
            />
            <label
              htmlFor="schema-picker-include-system"
              className="text-sm cursor-pointer"
            >
              Include system schemas{' '}
              <span className="text-muted-foreground">
                (sys, INFORMATION_SCHEMA, pg_catalog, …)
              </span>
            </label>
          </div>

          {/* Schema list */}
          <ScrollArea className="h-64 border rounded-md">
            <div className="py-1" data-testid="schema-picker-list">
              {availableSchemas.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  No schemas available. Pick a source and target database first.
                </div>
              ) : filteredSchemas.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  No matching schemas.
                </div>
              ) : (
                filteredSchemas.map((schema) => {
                  const isChecked = selected.has(schema);
                  return (
                    <label
                      key={schema}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer text-sm"
                      data-testid={`schema-picker-row-${schema}`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleSchema(schema)}
                        data-testid={`schema-picker-checkbox-${schema}`}
                      />
                      <span className="font-mono">{schema || '(default)'}</span>
                    </label>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="schema-picker-cancel"
          >
            Cancel
          </Button>
          <Button onClick={handleApply} data-testid="schema-picker-apply">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
